import React, { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { css } from '@emotion/react';
import { UploadEditImageIcon, UploadImageIcon } from 'public/assets';
import theme from '@styles/theme';

interface IProfileImagePopup {
  onSelectCancel: () => void;
  onSelectConfirm: (customProfile?: { file: File; fileUrl: string }) => void;
}

const ProfileImagePopup = ({
  onSelectCancel,
  onSelectConfirm,
}: IProfileImagePopup) => {
  const { t } = useTranslation();
  const [customProfile, setCustomProfile] = useState<{
    file: File;
    fileUrl: string;
  }>();

  const onCancel = () => {
    setCustomProfile(undefined);
    onSelectCancel();
  };

  const onConfirm = () => {
    onSelectConfirm(customProfile);
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files: FileList | null = e.target.files;
    if (files?.[0]) {
      const imageUrl = URL.createObjectURL(files[0]);
      files.item;
      setCustomProfile({
        file: files[0],
        fileUrl: imageUrl,
      });
    }
  };

  return (
    <div css={container}>
      <div className="title-container">
        <div className="title">{t('chat_popup_label_upload_image')}</div>
        {customProfile && (
          <label className="edit-button" htmlFor="profile-file">
            <UploadEditImageIcon />
          </label>
        )}
      </div>
      <input
        type="file"
        id="profile-file"
        style={{ display: 'none' }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          onImageChange(e);
        }}
        accept="image/png, image/jpeg"
      />
      {customProfile ? (
        <div className="image-box">
          <img
            className="image"
            src={customProfile.fileUrl}
            alt="Profile image"
          />
        </div>
      ) : (
        <div className="select-box">
          <div className="info-box">
            <UploadImageIcon />
            <div>{t('chat_popup_description_choose_image_file')}</div>
          </div>

          <label className="add-file" htmlFor="profile-file">
            {t('chat_popup_label_choose_file')}
          </label>
        </div>
      )}
      <div className="button-container">
        <div className="button cancel-button" onClick={onCancel}>
          {t('common_screen_button_cancel')}
        </div>
        <div className="button confirm-button" onClick={onConfirm}>
          {t('common_screen_title_confirm')}
        </div>
      </div>
    </div>
  );
};

const container = css`
  width: 100%;
  padding: 24px;
  border-radius: 12px;
  background-color: ${theme.colors.white};
  z-index: 2;

  .title-container {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
      font-family: 'Apple SD Gothic Neo';
      font-style: normal;
      font-weight: 700;
      font-size: 24px;
      line-height: 30px;
    }
    .edit-button {
      cursor: pointer;
    }
  }

  .select-box {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-top: 1em;
    height: 287px;
    width: 100%;
    border: 1px dashed ${theme.colors.gray300};
    border-radius: 8px;

    .info-box {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 10px;
      font-family: 'Apple SD Gothic Neo';
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: ${theme.colors.gray500};
    }

    .add-file {
      margin-top: 19px;
      font-family: 'Apple SD Gothic Neo';
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      padding: 12px 16px;
      border: 1px solid ${theme.colors.gray300};
      border-radius: 4px;
      display: flex;
      align-items: center;
      text-align: center;
      letter-spacing: -0.3px;
      color: ${theme.colors.gray900};
      cursor: pointer;
    }
  }

  .image-box {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-top: 1em;
    height: 287px;
    width: 100%;
    border: 1px solid ${theme.colors.gray300};
    border-radius: 8px;

    .image {
      height: 100%;
      max-width: 100%;
      border-radius: 8px;
    }
  }

  .button-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    font-family: 'Apple SD Gothic Neo';
    font-style: normal;
    font-size: 16px;
    line-height: 24px;
    margin-top: 24px;

    .button {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      padding: 13px 16px;
      border-radius: 8px;
      cursor: pointer;
    }

    .confirm-button {
      font-weight: 700;
      color: ${theme.colors.white};
      background-color: ${theme.colors.primary500};
    }

    .cancel-button {
      font-weight: 400;
      color: ${theme.colors.gray800};
      background-color: ${theme.colors.gray300};
    }
  }
`;

export default ProfileImagePopup;
